import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiClient, socketClient } from '../services/api';
import { User } from '../types';

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  register: (userData: {
    username: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    recaptchaToken?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<User>) => Promise<void>;
  verifyCode: (email: string, code: string, remember?: boolean) => Promise<any>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Migration: Clean up legacy token keys (authToken -> accessToken)
    const migrateTokens = () => {
      // Keys that should be migrated to accessToken if found
      const migrationMapping: Record<string, string> = {
        'authToken': 'accessToken',
        'auth_token': 'accessToken',
        'token': 'accessToken',
        'jwt': 'accessToken'
      };

      Object.entries(migrationMapping).forEach(([oldKey, newKey]) => {
        // Check localStorage
        const localVal = localStorage.getItem(oldKey);
        if (localVal && !localStorage.getItem(newKey)) {
          console.log(`🔄 Migrating legacy localStorage ${oldKey} to ${newKey}`);
          localStorage.setItem(newKey, localVal);
          localStorage.removeItem(oldKey);
        }

        // Check sessionStorage
        const sessionVal = sessionStorage.getItem(oldKey);
        if (sessionVal && !sessionStorage.getItem(newKey)) {
          console.log(`🔄 Migrating legacy sessionStorage ${oldKey} to ${newKey}`);
          sessionStorage.setItem(newKey, sessionVal);
          sessionStorage.removeItem(oldKey);
        }
      });

      // Keys to just clean up (not tokens)
      const cleanupKeys = ['user_id', 'temp_auth_state'];
      cleanupKeys.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });
    };

    // Run migration first
    migrateTokens();

    const inLocalStorage = !!localStorage.getItem('accessToken') || !!localStorage.getItem('refreshToken');
    const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');

    console.log('🗝️ Auth check:', {
      hasToken: !!token,
      hasRefreshToken: !!refreshToken,
      remembered: inLocalStorage
    });

    if (token || refreshToken) {
      console.log('🔄 Attempting to restore session...');
      // Explicitly set token on apiClient if available, preserving previous remember-me choice
      if (token) apiClient.setToken(token, inLocalStorage);

      const fetchUserWithRetry = async (retries = 3, delay = 2000) => {
        try {
          console.log(`📡 Fetching current user... (Attempt ${4 - retries})`);
          const userData = await apiClient.getCurrentUser();
          console.log('✅ Session restored for:', userData.email);
          setUser(userData);
          setIsLoading(false);
        } catch (err: any) {
          const status = err.status || (err.details && err.details.status);
          console.warn(`❌ Session restoration attempt failed (Status: ${status}):`, err.message);

          // ... (rest of logic)
          const isAuthError = status === 401 || status === 403 ||
            (err.message && (err.message.includes('Auth') || err.message.includes('token') || err.message.includes('Authentication failed')));

          const isNetworkError = err.message && (
            err.message.includes('Network') ||
            err.message.includes('fetch') ||
            err.name === 'AbortError' ||
            err.name === 'TypeError'
          );

          if (isAuthError && !isNetworkError) {
            console.log('🚪 Session explicitly invalid, clearing...');
            apiClient.clearToken(); // Clears both tokens
            setUser(null);
            setIsLoading(false);
          } else if (retries > 0) {
            console.log(`⏳ Retrying session restoration in ${delay}ms... (${retries} attempts left)`);
            setTimeout(() => fetchUserWithRetry(retries - 1, delay * 2), delay);
          } else {
            console.error('🚫 All session restoration attempts failed.', err);
            setIsLoading(false);
          }
        }
      };

      fetchUserWithRetry();
    } else {
      setIsLoading(false);
    }
  }, []);

  // Keep-Alive Mechanism to prevent Render from sleeping while using the app
  useEffect(() => {
    const keepAliveInterval = setInterval(() => {
      // Silent token refresh to keep session alive without triggering full re-auth
      // Silent token refresh to keep session alive without triggering full re-auth
      const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');

      if (token && refreshToken && user) {
        // Decode token to check if it's close to expiry
        try {
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            const expiryTime = payload.exp * 1000; // Convert to ms
            const currentTime = Date.now();
            const timeUntilExpiry = expiryTime - currentTime;

            // If token expires in less than 10 minutes, silently refresh it
            if (timeUntilExpiry < 10 * 60 * 1000) {
              console.log('🔄 Proactive token refresh (expires soon)');
              // Trigger a silent API call that will auto-refresh if needed
              apiClient.getCurrentUser().catch((err) => {
                // Only log errors, don't disrupt user
                if (err.message !== 'Authentication failed') {
                  console.log('Keep-alive check failed (background):', err.message);
                }
              });
            }
          }
        } catch (decodeError) {
          console.log('Keep-alive: Could not decode token');
        }
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(keepAliveInterval);
  }, [user]);

  // Connect/Disconnect Socket based on auth state
  useEffect(() => {
    const userId = user?.id || (user as any)?._id || (user as any)?._id;
    if (userId) {
      const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      if (token) {
        console.log('📡 Auth: Connecting socket for', user?.username || userId);
        socketClient.connect(token);
      }
    } else if (!isLoading) {
      console.log('📡 Auth: Disconnecting socket (no user)');
      socketClient.disconnect();
    }
  }, [user?.id, isLoading]);

  // Listen for global logout events (triggered by API 401s)
  useEffect(() => {
    const handleLogout = () => {
      console.log('🚪 Global logout event received');
      setUser(null);
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  const login = async (email: string, password: string, remember: boolean = true) => {
    setIsLoading(true);
    try {
      const data = await apiClient.login(email, password, remember);
      setUser(data.user);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: {
    username: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    recaptchaToken?: string;
  }) => {
    setIsLoading(true);
    try {
      const data = await apiClient.register(userData);
      setUser(data.user);
    } finally {
      setIsLoading(false);
    }
  };

  const verifyCode = async (email: string, code: string, remember: boolean = true) => {
    setIsLoading(true);
    try {
      const data = await apiClient.verifyLoginCode(email, code, remember);
      // If we get a token but no user object, fetch the user
      if (data.user) {
        setUser(data.user);
      } else {
        const userData = await apiClient.getCurrentUser();
        setUser(userData);
      }
      return data;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await apiClient.logout();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = async (updates: Partial<User>) => {
    if (!user) return;

    try {
      const response = await apiClient.updateUser(updates);
      // Backend returns { message, user, ... }
      if (response.user) {
        setUser(response.user);
      } else {
        // Fallback for old behavior or if API returns user directly
        setUser(response);
      }
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    updateUser,
    verifyCode,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
